/**
 * S7 reference-extraction × the canonical terminology index (W21 rung ⑤).
 *
 * The S7 card asks the human to sort each extracted reference term into
 * "standard vocabulary" vs "this screen only". The terminology index
 * (@cogo/validator/terminology — coogo's DEFINED vocabulary; terms are
 * defined and looked up, never inferred) is exactly the evidence that
 * question wants: a term the dictionary already defines is a strong
 * standard-vocabulary candidate; an undefined term is honestly marked as
 * not-yet-defined. The mark INFORMS the human's sorting — it never sorts
 * for them (S7's principle), and `undefinedTerm` is the index's own honest
 * signal, not a failure.
 */
import {
  resolveTerminology,
  terminologyScopes,
} from '@cogo/validator/terminology'

export interface TermDictionaryVerdict {
  term: string
  /** The index defines this exact term (or ranks a defined candidate). */
  inDictionary: boolean
  /** Present when the exact entry (or top candidate) carries a source. */
  source?: string
  /** True when the match is exact rather than a ranked candidate. */
  exact: boolean
  /** Which vocabulary defined it — 'platform' or an application scope. */
  scope?: string
}

type Resolver = (
  query: string,
  applicationScope?: string,
) => ReturnType<typeof resolveTerminology>

const defaultResolve: Resolver = (query, applicationScope) =>
  resolveTerminology(
    query,
    applicationScope ? { applicationScope } : {},
  )

/**
 * Dictionary EVIDENCE enumerates every scope EXPLICITLY: platform first,
 * then each application scope through the resolver's own documented
 * `applicationScope` option. Measured 2026-08-08: the resolver's pool
 * filter (`selectPool`) drops non-platform entries unless a scope is
 * named — an invariant, not a bug (it is the cross-application vocabulary
 * leak guard) — so the wonbanking banking glossary (이체, 계좌, …) was
 * invisible to the S7 marks, which mis-read as "Korean banking vocabulary
 * is entirely undefined". The verdict carries the defining SCOPE, so an
 * application-glossary hit is shown as exactly that: evidence for the
 * human sorter, never a cross-application resolution used by generation.
 */
export function checkTermsAgainstDictionary(
  terms: readonly string[],
  resolve: Resolver = defaultResolve,
  scopes: readonly string[] = terminologyScopes(),
): TermDictionaryVerdict[] {
  const applicationScopes = scopes.filter((scope) => scope !== 'platform')

  function verdictOf(
    term: string,
    result: ReturnType<Resolver>,
    matchedScope: string | undefined,
  ): TermDictionaryVerdict | null {
    if (result.exact) {
      return {
        term,
        inDictionary: true,
        exact: true,
        source: result.exact.source || undefined,
        scope: result.exact.scope || matchedScope,
      }
    }
    // Measured on the live index (2026-08-08): the similar-matcher offers a
    // WEAK candidate even for nonsense (score 0.64 on an unrelated id), so a
    // bare candidates check would stamp every chip 사전-등재. Only a
    // high-confidence surface counts as dictionary evidence.
    const top = result.candidates[0]
    if (
      !result.undefinedTerm &&
      top &&
      top.score >= 0.85 &&
      (top.matchedOn === 'term' || top.matchedOn === 'id')
    ) {
      return {
        term,
        inDictionary: true,
        exact: false,
        source: top.entry?.source || undefined,
        scope: top.entry?.scope || matchedScope,
      }
    }
    return null
  }

  return terms.map((term) => {
    // A weak platform candidate must not short-circuit the scope walk
    // (measured: platform fuzz offered 거래내역 a low-score candidate, which
    // hid the wonbanking glossary's real definition) — enumeration continues
    // until a scope yields dictionary-grade evidence, not merely any result.
    const platformVerdict = verdictOf(term, resolve(term), 'platform')
    if (platformVerdict) return platformVerdict
    for (const scope of applicationScopes) {
      const scopedVerdict = verdictOf(term, resolve(term, scope), scope)
      if (scopedVerdict) return scopedVerdict
    }
    return { term, inDictionary: false, exact: false }
  })
}
